import { FC, useEffect, useState } from 'react'
import axios from 'axios'
import clsx from 'clsx'
import type { User } from '../../../../app/modules/auth/core/_models'

type UserAvatarProps = {
  user: User | null | undefined
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

function getInitials(user: User | null | undefined): string {
  const first = user?.name?.charAt(0) ?? ''
  const second = user?.surname?.charAt(0) ?? ''
  return `${first}${second ? `.${second}` : ''}`.toUpperCase() || '?'
}

function buildFileUrl(filePath: string): string {
  return `${process.env.REACT_APP_API_BASE_URL}files/uploaded/${filePath}`
}

const sizeClassMap = {
  sm: 'symbol-35px',
  md: 'symbol-40px',
  lg: 'symbol-50px',
} as const

const UserAvatar: FC<UserAvatarProps> = ({ user, size = 'md', className }) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const initials = getInitials(user)
  const photoId = user?.profile_photo
  const hasPhoto = !!photoId && String(photoId) !== '0'

  useEffect(() => {
    let cancelled = false
    setPhotoUrl(null)

    if (!hasPhoto) {
      return
    }

    axios
      .get(`/files/getFileData/${photoId}`)
      .then((response) => {
        const filePath = response?.data?.data?.file_path
        if (!cancelled && filePath) {
          setPhotoUrl(buildFileUrl(filePath))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPhotoUrl(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [photoId, hasPhoto])

  return (
    <div className={clsx('symbol', sizeClassMap[size], className)}>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={user ? `${user.name ?? ''} ${user.surname ?? ''}`.trim() : 'User'}
          className='symbol-label'
          style={{ objectFit: 'cover' }}
          onError={() => setPhotoUrl(null)}
        />
      ) : (
        <div className='symbol-label fs-7 fw-bold text-white' style={{ background: '#181C32' }}>
          {initials}
        </div>
      )}
    </div>
  )
}

export { UserAvatar }
